import {
  useFetchSalesforceObjectFields,
  useFetchSalesforceObjects,
} from '@modules/integrations/components/EditSalesforceIntegration/hooks';
import { IntegrationNamePreview } from '@modules/integrations/components/IntegrationNamePreview';
import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import {
  useIsIntegrationContextEnabled,
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import { EditIntegrationDrawerContentProps } from '@modules/integrationsV2/types';
import { trackEditIntegrationEvent } from '@modules/integrationsV2/utils';
import { LoadingButton } from '@mui/lab';
import { Autocomplete, Box, Stack, TextField } from '@mui/material';
import {
  IntegrationChannelType,
  PrimitiveDataType,
  SalesforceDescribeField,
} from 'generated/graphql';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { AddOrEditIntegrationDrawerHeader } from '../../AddOrEditIntegrationDrawerHeader';

export const EditSalesforceIntegrationDrawerContent = ({
  onDrawerClose,
  integrationID,
  integrationData: { salesforce },
  integrationType,
  description,
}: EditIntegrationDrawerContentProps) => {
  const isIntegrationContextEnabled = useIsIntegrationContextEnabled();

  const [selectedObject, setSelectedObject] = useState<
    string | undefined | null
  >(salesforce?.objectSchema?.name);

  const [selectedFields, setSelectedFields] = useState<
    SalesforceDescribeField[]
  >([]);

  const [descriptionInput, setDescriptionInput] = useState(description);

  const { salesforceObjects, status: salesforceObjectsFetchStatus } =
    useFetchSalesforceObjects(integrationID);
  const [fetchSalesforceObjectFields, objectFields] =
    useFetchSalesforceObjectFields(integrationID, selectedObject || '');

  const textFields = useMemo(
    () =>
      objectFields.filter(
        (objectField) => objectField.type === PrimitiveDataType.String,
      ),
    [objectFields],
  );

  useEffect(() => {
    if (textFields?.length && salesforce?.objectSchema?.surveyContent) {
      const selectedTextFields = textFields.filter((textField) => {
        const isSelectedField = !!salesforce?.objectSchema?.surveyContent?.find(
          ({ name }) => {
            return name === textField.name;
          },
        );

        return isSelectedField;
      });

      setSelectedFields(selectedTextFields);
    }
  }, [salesforce?.objectSchema?.surveyContent, textFields]);

  useEffect(() => {
    if (!selectedObject) {
      return;
    }

    fetchSalesforceObjectFields({
      variables: {
        integrationID,
        salesforce: { describeObject: { objectName: selectedObject } },
      },
    });
  }, [integrationID, selectedObject, fetchSalesforceObjectFields]);

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.Salesforce,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
        trackEditIntegrationEvent({
          eventName: 'Edit Integration Submitted',
          channelType: IntegrationChannelType.Salesforce,
          integrationID,
        });
      },
    });

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidated,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.Salesforce,
    integrationType,
    hasValidFormFields: !!selectedObject && !!selectedFields.length,
    descriptionInput,
    currentDescription: description,
    onValidated: async () => {
      selectedObject &&
        updateIntegration({
          integrationID,
          isActive: true,
          updateData: {
            salesforce: {
              schema: {
                name: selectedObject,
                fieldsToResolve:
                  salesforce?.objectSchema?.fieldsToResolve || [],
                surveyContent: selectedFields.map(({ name, label }) => {
                  return {
                    name,
                    label,
                  };
                }),
              },
            },
          },
          updateDescription: {
            description,
          },
        });
    },
  });

  const onObjectChange = (_: SyntheticEvent, value: string | null) => {
    setSelectedObject(value);
  };

  const onFieldsChange = (
    _: SyntheticEvent,
    fields: SalesforceDescribeField[],
  ) => {
    setSelectedFields(fields);
  };

  const isSalesforceObjectsFetchLoading =
    !salesforceObjects.length && IsLoading(salesforceObjectsFetchStatus);

  const isLoading = IsLoading(updateIntegrationStatus);

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader title="Edit Salesforce Integration" />
        <Stack
          direction="column"
          spacing={4}
          p={3}
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            submitForm();
          }}
          autoComplete="off"
        >
          <Stack spacing={2}>
            <Autocomplete
              fullWidth
              value={selectedObject}
              options={salesforceObjects || []}
              onChange={onObjectChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Salesforce Object"
                  helperText={
                    selectedObject === 'Opportunity'
                      ? 'The object will be ingested when the Opportunity Stage goes to `Closed Win` or `Closed Lost`.'
                      : "The object will be ingested as soon as it is created. If you don't want this behaviour, please reach out to namaste@enterpret.com"
                  }
                />
              )}
              componentsProps={{ paper: { elevation: 8 } }}
              loading={isSalesforceObjectsFetchLoading}
              disabled={isLoading}
            />
            <Autocomplete
              fullWidth
              multiple
              disableCloseOnSelect
              limitTags={10}
              disabled={!textFields?.length || isLoading}
              value={selectedFields}
              onChange={onFieldsChange}
              options={textFields || []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Text Field(s)"
                  helperText="Predictions will be made on the above selected fields."
                />
              )}
              componentsProps={{ paper: { elevation: 8 } }}
              isOptionEqualToValue={(option, value) =>
                option.name === value?.name
              }
            />
            <IntegrationNamePreview
              integrationName={selectedObject ?? ''}
              sourceName={IntegrationChannelType.Salesforce}
            />
          </Stack>
          {isIntegrationContextEnabled && (
            <IntegrationDescriptionForm
              value={descriptionInput}
              onChange={setDescriptionInput}
              isValidationInProgress={isValidatingDescription}
              descriptionValidForQuestions={descriptionValidForQuestions}
              isRequired={isDescriptionRequired}
              validatedDescriptionMessage={validatedDescriptionMessage}
              isPreValidated={isPreValidated}
              integrationType={integrationType}
            />
          )}
          {!isIntegrationContextEnabled && (
            <LoadingButton
              disabled={isDisabled}
              fullWidth
              type="submit"
              size="large"
              variant="contained"
              loading={isLoading}
            >
              Save
            </LoadingButton>
          )}
        </Stack>
      </Box>
      {isIntegrationContextEnabled && (
        <AddOrEditIntegrationDrawerFooter
          onCancel={onDrawerClose}
          onSubmit={submitForm}
          isDisabled={isDisabled}
          isLoading={isLoading}
        />
      )}
    </Stack>
  );
};
